import { useEffect, useRef } from 'react';

import { SupportedLanguages, SupportedRegions } from '@rbi-ctg/frontend';
import { useLocale } from 'state/intl';
import {
  LOCALE_SELECTED_QUERY_PARAM,
  PROD_SUPPORTED_REGIONS,
  regionSpecificLocalStorageKeys,
} from 'state/intl/hook';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
// import { isNative } from 'utils/environment';
import { NEXT_URL_QUERY_PARAM } from 'state/location/constants';
import LocalStorage from 'utils/local-storage';

import { useLoyaltyActiveCard } from './use-loyalty-active-card';

enum BarCodeToCountry {
  '0464' = PROD_SUPPORTED_REGIONS.US,
  '0474' = PROD_SUPPORTED_REGIONS.US,
  '0463' = PROD_SUPPORTED_REGIONS.CA,
  '0473' = PROD_SUPPORTED_REGIONS.CA,
}

interface SetLanguageOptions {
  region: SupportedRegions;
  language: SupportedLanguages;
}

interface SetLanguage {
  (options: SetLanguageOptions): void;
}

const setLanguage: SetLanguage = ({ region, language: lang }) => {
  regionSpecificLocalStorageKeys.forEach(LocalStorage.removeItem);

  const params = new URLSearchParams({
    lang,
    region,
    [LOCALE_SELECTED_QUERY_PARAM]: '1',
    [NEXT_URL_QUERY_PARAM]: encodeURIComponent(window.location.pathname),
  });

  window.location.href = `${window.location.origin}?${params.toString()}`;
};

// NOTE: This should be a noop for all brands EXCEPT TH. We only need this code
// for a short time in order to redirect US users stuck on the TH CA native experience.
// SEE: https://rbictg.atlassian.net/browse/SHOP-2103

const useLoyaltyRedirect = () => {
  const { region } = useLocale();
  const enableLoyaltyRedirect = useFlag(LaunchDarklyFlag.ENABLE_TH_LOYALTY_REDIRECT);
  const language = 'en'; // US only uses 'en' as language

  const { activeCard } = useLoyaltyActiveCard();

  const loyaltyRegion: SupportedRegions | undefined =
    BarCodeToCountry[activeCard?.barcode?.substr(0, 4) || ''];

  const hasChecked = useRef(false);

  useEffect(() => {
    if (
      !enableLoyaltyRedirect ||
      hasChecked.current ||
      typeof loyaltyRegion === 'undefined' ||
      typeof region === 'undefined'
    ) {
      return;
    }

    hasChecked.current = true;

    if (loyaltyRegion === 'US' && region === 'CA') {
      setLanguage({ region: loyaltyRegion, language });
    }
  }, [region, loyaltyRegion, enableLoyaltyRedirect]);
};

export default useLoyaltyRedirect;
